import React from 'react';
import PropTypes from 'prop-types';
import Box from 'common/components/Box';
import Text from 'common/components/Text';
import Heading from 'common/components/Heading';
import Container from 'common/components/UI/Container';
import TrialDownloadButton from '../../Custom/TrialDownloadButton';
import ServiceMap from './ServiceMap';
import {
  CostInsightsWrapper,
  HighlightGrid,
  Highlight,
  ServiceGrid,
  ServiceCategory,
  ChipsLabel,
  CtaWrapper,
} from './costinsights.style';

const HIGHLIGHTS = [
  {
    title: 'Orphaned EBS volumes & snapshots',
    text:
      'Finds unattached volumes, volumes still paid for on stopped instances, and snapshot cleanup opportunities — a classic source of silent AWS waste.',
  },
  {
    title: 'GP2 → GP3 & Volume Type Advisor',
    text:
      'Recommends the cheapest EBS volume type that still meets your workload needs, based on 14 days of CloudWatch metrics.',
  },
  {
    title: 'Extended Support alerts',
    text:
      'Spots EKS clusters and RDS databases quietly adding hundreds of dollars a month in Extended Support charges — including the total paid so far.',
  },
  {
    title: 'Idle & oversized resources',
    text:
      'Detects idle RDS databases and underutilized EC2 instances, with rightsizing recommendations up to Auto Scaling Group level.',
  },
  {
    title: 'GenAI cost attribution',
    text:
      'Breaks down Claude spend on Amazon Bedrock by team using IAM principal tags, so you know who is generating the GenAI bill. Shown in its own Costs by IAM tag tab, outside the Savings Scan.',
  },
  {
    title: 'Run everything at once',
    text:
      'A Savings Scan analyzes the supported services that have costs in the selected month, in one pass. If a permission is missing, CloudPouch tells you exactly which API call failed, and never confuses "couldn\'t check" with "nothing to save". Individual Cost Insights stay available for a closer look at one service.',
  },
];

const SERVICE_CATEGORIES = [
  {
    category: 'Compute',
    services: [
      {
        name: 'EC2',
        text:
          'Unused, barely used and oversized instances ranked by estimated savings, using CloudWatch metrics and AWS Compute Optimizer, up to Auto Scaling Group level.',
      },
      {
        name: 'Lambda',
        text:
          'Cost per function with memory, duration and invocations. Flags functions that are never invoked, error waste, arm64 (Graviton) candidates, overlong timeouts and deprecated runtimes.',
      },
      {
        name: 'EKS',
        text:
          'Clusters paying the Extended Support surcharge for outdated Kubernetes versions — monthly charge and total paid since enrollment.',
      },
    ],
  },
  {
    category: 'Storage',
    services: [
      {
        name: 'EBS',
        text:
          'Unattached volumes, volumes on stopped instances, and a Volume Type Advisor that picks the cheapest volume type from 14 days of usage metrics.',
      },
      {
        name: 'EBS Snapshots',
        text:
          'Block-level savings from deleting or archiving snapshots, computed on incremental chains — with delete vs. archive recommendations.',
      },
      {
        name: 'S3',
        text:
          'Cost per bucket by storage class, missing lifecycle policies, Intelligent-Tiering opportunities, empty buckets and encryption gaps.',
      },
      {
        name: 'EFS',
        text:
          'Storage-class mix per file system, missing lifecycle policies, Provisioned Throughput waste, and file systems without mount targets.',
      },
      {
        name: 'ECR',
        text:
          'Outdated and unused container images per repository, with estimated savings and lifecycle-policy status.',
      },
    ],
  },
  {
    category: 'Databases',
    services: [
      {
        name: 'RDS',
        text:
          'Idle databases, over-provisioned instances and storage issues — plus RDS Extended Support detection with monthly and historical cost.',
      },
      {
        name: 'DynamoDB',
        text:
          'Provisioned vs. actually consumed throughput, over-provisioned tables, autoscaling recommendations, and burst-capacity risk.',
      },
      {
        name: 'DocumentDB',
        text:
          'Idle clusters, rightsizing opportunities, and specific Graviton migration candidates.',
      },
    ],
  },
  {
    category: 'Networking',
    services: [
      {
        name: 'VPC',
        text:
          'Missing free Gateway Endpoints for S3 and DynamoDB, which send that traffic through a paid NAT Gateway.',
      },
      {
        name: 'NAT Gateway',
        text:
          'Gateways with no outbound traffic for 14 days, to delete or double-check.',
      },
      {
        name: 'ELB',
        text:
          'Unused Application and Network Load Balancers with no meaningful traffic or empty target groups.',
      },
      {
        name: 'Elastic IP',
        text:
          'Idle addresses — unassociated or on stopped instances — billed hourly under the 2024 all-public-IPv4 pricing.',
      },
    ],
  },
  {
    category: 'Analytics',
    services: [
      {
        name: 'Glue',
        text:
          'Crawlers that never ran, plus job rightsizing, Flex execution class and auto scaling opportunities.',
      },
    ],
  },
  {
    category: 'Observability',
    services: [
      {
        name: 'CloudWatch Logs',
        text:
          'Ingestion and storage cost per log group, retention settings, growth tracking, and never-expiring log groups (the AWS default).',
      },
    ],
  },
];

const CostInsightsSection = ({
  sectionWrapper,
  secTitleWrapper,
  secText,
  secHeading,
  intro,
}) => {
  return (
    <CostInsightsWrapper id="cost_insights">
      <Container>
        <Box {...sectionWrapper}>
          <Box {...secTitleWrapper}>
            <Text {...secText} content="COST INSIGHTS" />
            <Heading
              {...secHeading}
              content="Savings recommendations with dollar amounts — not just charts"
            />
            <Text
              {...intro}
              content="Cost Insights scan the resources in your AWS accounts and tell you what to fix, why it costs money, and how much you will save. Read-only, running on your computer."
            />
          </Box>
          <ServiceMap />
          <HighlightGrid>
            {HIGHLIGHTS.map((item, index) => (
              <Highlight key={`ci-highlight-${index}`}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Highlight>
            ))}
          </HighlightGrid>
          <ChipsLabel>
            WHAT CLOUDPOUCH CHECKS, AND COUNTING
          </ChipsLabel>
          <ServiceGrid>
            {SERVICE_CATEGORIES.map((group) => (
              <ServiceCategory key={group.category}>
                <h3>{group.category}</h3>
                <dl>
                  {group.services.map((service) => (
                    <React.Fragment key={service.name}>
                      <dt>{service.name}</dt>
                      <dd>{service.text}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </ServiceCategory>
            ))}
          </ServiceGrid>
          <CtaWrapper>
            <TrialDownloadButton location="cost_insights" />
          </CtaWrapper>
        </Box>
      </Container>
    </CostInsightsWrapper>
  );
};

CostInsightsSection.propTypes = {
  sectionWrapper: PropTypes.object,
  secTitleWrapper: PropTypes.object,
  secText: PropTypes.object,
  secHeading: PropTypes.object,
  intro: PropTypes.object,
};

CostInsightsSection.defaultProps = {
  sectionWrapper: {},
  secTitleWrapper: {
    mb: ['30px', '40px'],
  },
  secText: {
    as: 'span',
    display: 'block',
    textAlign: 'center',
    fontSize: '14px',
    letterSpacing: '0.15em',
    fontWeight: '700',
    color: '#46679f',
    mb: '5px',
  },
  secHeading: {
    textAlign: 'center',
    fontSize: ['20px', '24px'],
    fontWeight: '500',
    color: '#0f2137',
    letterSpacing: '-0.025em',
    mb: '15px',
    lineHeight: '1.67',
  },
  intro: {
    textAlign: 'center',
    fontSize: ['15px', '16px'],
    color: '#343d48cc',
    lineHeight: '1.75',
    mb: '0',
    maxWidth: '720px',
    ml: 'auto',
    mr: 'auto',
  },
};

export default CostInsightsSection;
